<template>
    <!-- Large-image preview (owner 2026-10-15) as a component: the one
         lightbox shared by the /seller/orders inline order detail
         (SellerOrderDetail.vue) and /seller/products (views/AppCatalog/
         Products.vue, which carried its own copy until bo-fn #475). Lifted
         byte-identical into mdf-seller-fn.

         Usage: <image-preview-lightbox :preview="p" @close="p = null" />
         with p = { src, caption, trigger? } (trigger = the element focus goes
         back to on close; defaults to whatever had focus when it opened) or
         null (closed). The src is rendered byte for byte (signed CDN URLs).

         The root is moved to <body> on mount (a portal): inside a page it can
         sit under a sticky / transformed ancestor (the /seller/orders panel is
         in `.so-detail-sticky`, a stacking context below the z 1050 sidebar),
         where no z-index lifts it. On <body>: position: fixed, z 3000 (above
         el-dialog and the sidebar). Closes on ×, Esc and the backdrop; focus
         moves to × on open, Tab stays on it, focus returns on close; body
         scroll is locked while open and released on close, keep-alive
         deactivation and destroy. -->
    <div class="mdf-lightbox-portal" data-testid="image-preview-portal">
        <div
            v-if="preview"
            class="mdf-lightbox"
            role="dialog"
            aria-modal="true"
            :aria-label="preview.caption || $t('app_catalog.col_image')"
            data-testid="image-preview-lightbox"
            @click.self="close"
        >
            <button
                ref="close"
                type="button"
                class="btn btn-sm btn-white mdf-lightbox-close"
                data-testid="image-preview-lightbox-close"
                :aria-label="$t('app_catalog.image_preview_close')"
                :title="$t('app_catalog.image_preview_close')"
                @click="close"
            >&times;</button>
            <figure class="mdf-lightbox-figure" @click.self="close">
                <div
                    v-if="state === 'loading'"
                    class="mdf-lightbox-status"
                    data-testid="image-preview-lightbox-loading"
                    role="status"
                >
                    <i class="fa fa-spinner fa-spin mr-2"></i>{{ $t("app_catalog.image_preview_loading") }}
                </div>
                <div
                    v-else-if="state === 'error'"
                    class="mdf-lightbox-status"
                    data-testid="image-preview-lightbox-error"
                    role="alert"
                >
                    <i class="fa fa-image mr-2"></i>{{ $t("app_catalog.image_preview_error") }}
                </div>
                <img
                    v-show="state === 'loaded'"
                    :key="preview.src"
                    :src="preview.src"
                    :alt="preview.caption || ''"
                    class="mdf-lightbox-img"
                    data-testid="image-preview-lightbox-img"
                    @load="state = 'loaded'"
                    @error="state = 'error'"
                />
                <figcaption v-if="preview.caption" class="mdf-lightbox-caption" data-testid="image-preview-lightbox-caption">{{ preview.caption }}</figcaption>
            </figure>
        </div>
    </div>
</template>

<script>
import { isEscapeKey, isTabKey } from "@/utils/imagePreview";

export default {
    name: "ImagePreviewLightbox",
    props: {
        // { src, caption, trigger? } = open, null = closed.
        preview: { type: Object, default: null }
    },
    data() {
        return {
            // "loading" until the <img> fires load / error.
            state: "loading"
        };
    },
    watch: {
        preview(now, before) {
            if (now && !before) this.activate(now);
            else if (!now && before) this.release(true);
            else if (now && before && now.src !== before.src) this.state = "loading";
        }
    },
    mounted() {
        // Portal: see the template comment.
        if (this.$el && this.$el.parentNode !== document.body) document.body.appendChild(this.$el);
        if (this.preview) this.activate(this.preview);
    },
    deactivated() {
        // keep-alive: leaving the page must not leave the body scroll locked.
        if (this.preview) this.$emit("close");
        this.release(false);
    },
    beforeDestroy() {
        this.release(false);
    },
    destroyed() {
        // Vue only detaches the root of a removed subtree; ours lives on <body>.
        if (this.$el && this.$el.parentNode) this.$el.parentNode.removeChild(this.$el);
    },
    methods: {
        activate(p) {
            this.state = "loading";
            if (!this.active) {
                this.active = true;
                this.bodyOverflow = document.body.style.overflow;
                document.body.style.overflow = "hidden";
                document.addEventListener("keydown", this.onKey);
                // Focus returns here on close (WAI-ARIA dialog pattern).
                this.returnFocus = (p && p.trigger) || document.activeElement || null;
            }
            this.$nextTick(() => {
                const btn = this.$refs.close;
                if (btn && btn.focus) btn.focus();
            });
        },
        release(restoreFocus) {
            if (!this.active) return;
            this.active = false;
            this.state = "loading";
            document.body.style.overflow = this.bodyOverflow || "";
            this.bodyOverflow = "";
            document.removeEventListener("keydown", this.onKey);
            const back = this.returnFocus;
            this.returnFocus = null;
            if (restoreFocus && back && back.focus && document.body.contains(back)) back.focus();
        },
        close() {
            this.$emit("close");
        },
        onKey(e) {
            if (isEscapeKey(e)) {
                e.preventDefault();
                this.close();
                return;
            }
            // Focus trap: × is the dialog's only focusable control, so Tab and
            // Shift+Tab both stay on it instead of walking into the page behind.
            if (isTabKey(e)) {
                e.preventDefault();
                const btn = this.$refs.close;
                if (btn && btn.focus) btn.focus();
            }
        }
    }
};
</script>

<style scoped>
/* Same rules as OrdersNew.vue's `.bon-lightbox`: above el-dialog (z 2000+),
   dark backdrop. */
.mdf-lightbox {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 3000;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
}
.mdf-lightbox-figure {
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 90vw;
}
.mdf-lightbox-img {
    display: block;
    max-width: 90vw;
    max-height: 85vh;
    object-fit: contain;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}
/* Caption + status sit on their own opaque dark pill, so the white text is
   scored (and read) against #212529, never against the photo. */
.mdf-lightbox-caption {
    margin-top: 12px;
    max-width: 90vw;
    padding: 6px 12px;
    border-radius: 6px;
    color: #fff;
    background-color: #212529;
    font-size: 1rem;
    text-align: center;
    word-break: break-word;
}
.mdf-lightbox-status {
    border-radius: 6px;
    color: #fff;
    background-color: #212529;
    font-size: 1rem;
    padding: 24px;
}
.mdf-lightbox-close {
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 1.25rem;
    line-height: 1;
    padding: 4px 10px;
}
</style>
